/**
 * Unified Vehicle API — single-schema /api/vehicles CRUD.
 * Replaces fragmented valt-vehicles, go-metro-vehicles, aeversa-vehicles endpoints.
 */
import {
  getAuthHeaders,
  parseValtVehicleResponseRegion,
  valtFetch,
  VALT_API_BASE,
} from '@/utils/api'
import {
  appendRegionScopeToParams,
  regionScopeHeaders,
} from '@/services/regionScope'
import { DEFAULT_PAGE_SIZE } from '@/config/fleetConstants'
import type {
  UnifiedVehicle,
  UnifiedVehicleFilters,
  UnifiedVehicleListResponse,
  VehicleDriverLink,
} from '@/types/vehicle'
import { parseVehicleDriverLink } from '@/utils/vehicleDriverLink'

export interface CreateUnifiedVehicleData {
  vin?: string | null
  car_make?: string | null
  model?: string | null
  number_plate?: string | null
  colour?: string | null
  year?: number | string | null
  device_id?: string | null
  uber_vehicle_uuid?: string | null
  aeversa_id_tag?: string | null
  region_id?: number | null
  go_metro_vehicle_id?: number | null
  initial_linked_driver?: string | null
  primary_status?: string | null
  sub_status?: string | null
  /** Asset classification (text slug from vehicle_types table, e.g. bike). */
  vehicle_type?: string | null
  /** Server-side filter on live immobilisation state; omit for no filter. */
  immobilised?: boolean | null
  po_number?: string | null
  po_batch?: string | null
  po_date?: string | null
  po_order_date?: string | null
  po_landed_date?: string | null
}

export class UnifiedVehicleApiService {
  private static readonly API_BASE_URL = VALT_API_BASE

  private static buildUrl(path: string, params?: URLSearchParams): string {
    const qs = params?.toString()
    return `${this.API_BASE_URL}/vehicles${path}${qs ? `?${qs}` : ''}`
  }

  private static listParams(filters?: UnifiedVehicleFilters): URLSearchParams {
    const params = new URLSearchParams()
    if (filters?.page) params.set('page', String(filters.page))
    params.set('per_page', String(filters?.per_page ?? filters?.limit ?? DEFAULT_PAGE_SIZE))

    if (filters?.vin) params.set('vin', filters.vin)
    if (filters?.number_plate) params.set('number_plate', filters.number_plate)
    if (filters?.car_make) params.set('car_make', filters.car_make)
    if (filters?.model) params.set('model', filters.model)
    if (filters?.colour) params.set('colour', filters.colour)
    if (filters?.primary_status) params.set('primary_status', filters.primary_status)
    if (filters?.status) params.set('primary_status', filters.status)
    if (filters?.vehicle_type) params.set('vehicle_type', filters.vehicle_type)
    // Explicit null check: `false` is a real filter (show only mobile vehicles), not "unset".
    if (filters?.immobilised != null) params.set('immobilised', filters.immobilised ? 'true' : 'false')

    return params
  }

  private static scopedParams(filters?: UnifiedVehicleFilters): URLSearchParams {
    const params = this.listParams(filters)

    if (filters?.region_id != null) {
      params.set('region_id', String(filters.region_id))
    }

    return appendRegionScopeToParams(params, filters?.region)
  }

  private static scopedHeaders(region?: string): Record<string, string> {
    return { ...getAuthHeaders(), ...regionScopeHeaders(region) }
  }

  private static attachRegion(v: UnifiedVehicle, json: Record<string, unknown>): UnifiedVehicle {
    const apiRegion = parseValtVehicleResponseRegion(json)
    if (apiRegion) v.api_region = apiRegion
    return v
  }

  static async list(filters?: UnifiedVehicleFilters): Promise<UnifiedVehicleListResponse> {
    const params = this.listParams(filters)
    const response = await valtFetch(this.buildUrl('', params), {
      method: 'GET',
      headers: getAuthHeaders(),
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch vehicles: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as Record<string, unknown>
    const rawData = (json.data ?? json.vehicles ?? []) as UnifiedVehicle[]
    const pagination = (json.pagination ?? json.meta ?? {}) as Record<string, number>
    const perPage = pagination.per_page ?? filters?.per_page ?? DEFAULT_PAGE_SIZE
    const lastPage = pagination.last_page

    const data = rawData.map((v) => this.attachRegion(v, json))

    return {
      success: Boolean(json.success ?? true),
      data,
      pagination: {
        current_page: pagination.current_page ?? filters?.page ?? 1,
        per_page: perPage,
        total:
          pagination.total ??
          (lastPage != null && lastPage > 1 ? lastPage * perPage : data.length),
        last_page: lastPage ?? 1,
        from: pagination.from,
        to: pagination.to,
      },
      region: json.region as string | undefined,
      region_id: json.region_id as number | undefined,
      connection: json.connection as string | undefined,
    }
  }

  static async getById(id: number | string, _opts?: { region?: string }): Promise<UnifiedVehicle | null> {
    const response = await valtFetch(this.buildUrl(`/${encodeURIComponent(String(id))}`), {
      method: 'GET',
      headers: getAuthHeaders(),
    })

    if (response.status === 404) return null
    if (!response.ok) {
      throw new Error(`Failed to fetch vehicle: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as Record<string, unknown>
    const vehicle = (json.data ?? json) as UnifiedVehicle
    return this.attachRegion(vehicle, json)
  }

  static async create(data: CreateUnifiedVehicleData, region?: string): Promise<UnifiedVehicle> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const qs = params.toString()

    const response = await valtFetch(this.buildUrl(qs ? `?${qs}` : ''), {
      method: 'POST',
      headers: this.scopedHeaders(region),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create vehicle: ${response.status} - ${errorText}`)
    }

    const json = (await response.json()) as Record<string, unknown>
    return (json.data ?? json) as UnifiedVehicle
  }

  static async update(
    id: number,
    data: Partial<CreateUnifiedVehicleData>,
    region?: string,
  ): Promise<UnifiedVehicle> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const qs = params.toString()

    const response = await valtFetch(this.buildUrl(`/${id}${qs ? `?${qs}` : ''}`), {
      method: 'PATCH',
      headers: this.scopedHeaders(region),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update vehicle: ${response.status} - ${errorText}`)
    }

    const json = (await response.json()) as Record<string, unknown>
    return (json.data ?? json) as UnifiedVehicle
  }

  static async delete(id: number | string, region?: string): Promise<boolean> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const qs = params.toString()

    const response = await valtFetch(this.buildUrl(`/${encodeURIComponent(String(id))}${qs ? `?${qs}` : ''}`), {
      method: 'DELETE',
      headers: this.scopedHeaders(region),
    })

    if (response.status === 404) {
      const body = await response.json().catch(() => ({}))
      throw new Error((body as { message?: string }).message || 'Vehicle not found')
    }

    if (!response.ok) {
      throw new Error(`Failed to delete vehicle: ${response.status}`)
    }

    return true
  }

  // ── Sub-resources ──────────────────────────────────────────

  static async getTelemetry(id: number, region?: string): Promise<Record<string, unknown>> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/telemetry?${params}`), {
      headers: this.scopedHeaders(region),
    })
    if (!response.ok) throw new Error(`Failed to fetch telemetry: ${response.status}`)
    const json = await response.json()
    return (json.data ?? json) as Record<string, unknown>
  }

  static async getCharging(id: number, region?: string, limit = 200): Promise<Record<string, unknown>> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    params.set('limit', String(limit))
    const response = await valtFetch(this.buildUrl(`/${id}/charging?${params}`), {
      headers: this.scopedHeaders(region),
    })
    if (!response.ok) throw new Error(`Failed to fetch charging: ${response.status}`)
    const json = await response.json()
    return (json.data ?? json) as Record<string, unknown>
  }

  static async assignDevice(id: number, deviceId: string, region?: string): Promise<UnifiedVehicle> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/device?${params}`), {
      method: 'POST',
      headers: this.scopedHeaders(region),
      body: JSON.stringify({ device_id: deviceId }),
    })
    if (!response.ok) throw new Error(`Failed to assign device: ${response.status}`)
    const json = await response.json()
    return (json.data ?? json) as UnifiedVehicle
  }

  static async unassignDevice(id: number, region?: string): Promise<void> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/device?${params}`), {
      method: 'DELETE',
      headers: this.scopedHeaders(region),
    })
    if (!response.ok) throw new Error(`Failed to unassign device: ${response.status}`)
  }

  static async assignChargingTag(id: number, aeversaIdTag: string, region?: string): Promise<UnifiedVehicle> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/charging-tag?${params}`), {
      method: 'POST',
      headers: this.scopedHeaders(region),
      body: JSON.stringify({ aeversa_id_tag: aeversaIdTag }),
    })
    if (!response.ok) throw new Error(`Failed to assign charging tag: ${response.status}`)
    const json = await response.json()
    return (json.data ?? json) as UnifiedVehicle
  }

  static async unassignChargingTag(id: number, region?: string): Promise<void> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/charging-tag?${params}`), {
      method: 'DELETE',
      headers: this.scopedHeaders(region),
    })
    if (!response.ok) throw new Error(`Failed to unassign charging tag: ${response.status}`)
  }

  static async getDriver(id: number, region?: string): Promise<VehicleDriverLink | null> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/driver?${params}`), {
      headers: this.scopedHeaders(region),
    })
    if (response.status === 404) return null
    if (!response.ok) throw new Error(`Failed to fetch driver: ${response.status}`)
    const json = await response.json()
    return parseVehicleDriverLink(json)
  }

  static async assignDriver(id: number, driverId: string, region?: string): Promise<UnifiedVehicle> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/driver?${params}`), {
      method: 'POST',
      headers: this.scopedHeaders(region),
      body: JSON.stringify({ driver_id: driverId }),
    })
    if (!response.ok) throw new Error(`Failed to assign driver: ${response.status}`)
    const json = await response.json()
    return (json.data ?? json) as UnifiedVehicle
  }

  static async unassignDriver(id: number, region?: string): Promise<void> {
    const params = new URLSearchParams()
    appendRegionScopeToParams(params, region)
    const response = await valtFetch(this.buildUrl(`/${id}/driver?${params}`), {
      method: 'DELETE',
      headers: this.scopedHeaders(region),
    })
    if (!response.ok) throw new Error(`Failed to unassign driver: ${response.status}`)
  }

  static async getStatusOptions(): Promise<Record<string, unknown>> {
    const response = await valtFetch(this.buildUrl('/status-options'), {
      headers: getAuthHeaders(),
    })
    if (!response.ok) throw new Error(`Failed to fetch status options: ${response.status}`)
    const json = await response.json()
    return (json.data ?? json) as Record<string, unknown>
  }
}
